import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown at the left of the rail header. */
  label?: string;
  /** e.g. "3/7" */
  count?: string;
  /** Optional action at the right of the header. */
  action?: ReactNode;
}

/**
 * A horizontal rail of choices.
 *
 * Wrapping grids were the reason the shoot page ran to eight thousand pixels:
 * five groups of poses alone stacked to several screens. One row per group
 * keeps every group visible at once, scrolls by swipe on a phone and by the
 * arrows on a desktop, and snaps so cards never stop half-cut.
 */
export function Carousel({ children, label, count, action }: Props) {
  const rail = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = rail.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    // A one-pixel tolerance: fractional widths never land exactly on the end.
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = rail.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, children]);

  const page = (direction: 1 | -1) => {
    const el = rail.current;
    if (!el) return;
    // Leave a card of overlap so you never lose your place.
    el.scrollBy({ left: direction * (el.clientWidth * 0.8), behavior: 'smooth' });
  };

  const hasArrows = !(atStart && atEnd);

  return (
    <div className="rail">
      {(label || count || action || hasArrows) && (
        <div className="rail__head">
          {label && <span className="label">{label}</span>}
          {count && <span className="rail__count">{count}</span>}
          <span className="rail__spacer" />
          {action}
          {hasArrows && (
            <span className="rail__arrows">
              <button
                type="button"
                className="rail__arrow"
                onClick={() => page(-1)}
                disabled={atStart}
                aria-label={`Scroll ${label ?? 'options'} left`}
              >
                ‹
              </button>
              <button
                type="button"
                className="rail__arrow"
                onClick={() => page(1)}
                disabled={atEnd}
                aria-label={`Scroll ${label ?? 'options'} right`}
              >
                ›
              </button>
            </span>
          )}
        </div>
      )}

      <div className="rail__track" ref={rail} onScroll={measure}>
        {children}
      </div>
    </div>
  );
}
